import type { TamCalculatorResults } from "~/react/types/tam_calculator"
import { TamResultsTable } from "~/react/components/tam_calculator_results/tam_results_table"
import { LoadingSpinner } from "~/react/ui/loading-spinner"
import { PageHeader, PageHeaderTitle, PageHeaderActions } from "~/react/ui/page-header"
import { Button } from "~/react/ui/button"
import emptyStateImageUrl from "~/images/empty_states/data-no-data.svg"
import noResultsImageUrl from "~/images/empty_states/data-no-results.svg"

const t = (key: string, options?: Record<string, unknown>) => window.i18n.t(key, options)

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

interface TamCalculatorResultsProps {
  results: TamCalculatorResults | null
  hasFilters: boolean
  isLoading: boolean
  acv: number | null
  tam: number | null
  isUserLoggedIn?: boolean
}

export function TamCalculatorResults({
  results,
  hasFilters,
  isLoading,
  acv,
  tam,
  isUserLoggedIn,
}: TamCalculatorResultsProps) {
  // Show empty state when no filters are applied
  if (!hasFilters && !isLoading) {
    return (
      <div className="tam-calculator__empty-state">
        <div className="tam-calculator__empty-state-img">
          <img src={emptyStateImageUrl} alt="" />
        </div>
        <h3 className="tam-calculator__empty-state-title">{t("js.visitors.tam_calculator.empty_state.title")}</h3>
        <p className="tam-calculator__empty-state-description">
          {t("js.visitors.tam_calculator.empty_state.description")}
        </p>
      </div>
    )
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="tam-calculator__loading">
        <LoadingSpinner />
        <p className="tam-calculator__loading-text">{t("js.visitors.tam_calculator.loading")}</p>
      </div>
    )
  }

  // Show results
  if (results && results.numberOfCompanies > 0) {
    return (
      <div className="tam-calculator__results">
        <PageHeader className="mb-0">
          <PageHeaderTitle>
            {tam !== null && tam > 0 ? (
              <>
                <span className="tam-calculator__tam-label">{t("js.visitors.tam_calculator.tam_display.label")}</span>
                <span className="tam-calculator__tam-value">{formatCurrency(tam)}</span>
              </>
            ) : (acv === null || acv === 0) && hasFilters ? (
              <>
                <span className="tam-calculator__tam-label">{t("js.visitors.tam_calculator.tam_display.label")}</span>
                <span className="tam-calculator__tam-value">{formatCurrency(0)}</span>
              </>
            ) : (
              <span>{t("js.visitors.tam_calculator.results_section.label")}</span>
            )}
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button variant="primary" asChild>
              <a href={isUserLoggedIn ? "/discover" : "/users/sign_up?from=tam_calculator"}>
                {t("js.visitors.tam_calculator.signup_cta.button", {
                  count: results.numberOfCompanies,
                  companies: results.numberOfCompanies.toLocaleString(),
                })}
              </a>
            </Button>
          </PageHeaderActions>
        </PageHeader>
        <TamResultsTable domains={results.domains} />
      </div>
    )
  }

  // Show no results state
  if (hasFilters) {
    return (
      <div className="tam-calculator__no-results">
        <div className="tam-calculator__no-results-img">
          <img src={noResultsImageUrl} alt="" />
        </div>
        <h3 className="tam-calculator__no-results-title">{t("js.visitors.tam_calculator.no_results.title")}</h3>
        <p className="tam-calculator__no-results-description">
          {t("js.visitors.tam_calculator.no_results.description")}
        </p>
      </div>
    )
  }

  return null
}
